import { Platform } from 'react-native';

/**
 * WordPress domains that serve media files.
 * Files from these domains will be proxied through our API to avoid CORS issues.
 */
const WORDPRESS_MEDIA_HOSTS = [
  'www.openstudiojazz.com',
  'openstudiojazz.com',
  'openstudio-test.mystagingwebsite.com',
];

/**
 * S3 bucket used for course audio/media backups.
 */
const S3_MEDIA_HOSTS = [
  'osn-course-backups.s3.us-west-2.amazonaws.com',
];

/**
 * Checks if a URL is a WordPress media URL that needs proxying.
 */
function isWordPressMediaUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return (
      WORDPRESS_MEDIA_HOSTS.includes(parsed.hostname) &&
      parsed.pathname.startsWith('/wp-content/uploads/')
    );
  } catch {
    return false;
  }
}

/**
 * Checks if a URL is an S3 media URL that should be proxied (e.g. audio files).
 */
function isS3MediaUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return S3_MEDIA_HOSTS.includes(parsed.hostname);
  } catch {
    return false;
  }
}

/**
 * Returns a proxied URL for WordPress media files on web platform.
 * On native platforms, returns the original URL (no CORS restrictions).
 * In development, returns the original URL (serverless function not available).
 *
 * @param url - The original media URL
 * @returns The proxied URL (web production) or original URL (native/dev)
 */
export function getProxiedMediaUrl(url: string): string {
  // Only proxy on web platform
  if (Platform.OS !== 'web') {
    return url;
  }

  // In development, use original URL (serverless function not available locally)
  if (__DEV__) {
    return url;
  }

  // Proxy WordPress and S3 media URLs
  if (!isWordPressMediaUrl(url) && !isS3MediaUrl(url)) {
    return url;
  }

  // Proxy through our Vercel serverless function (production only)
  return `/api/media?url=${encodeURIComponent(url)}`;
}
